import type { TextDocument } from 'vscode-languageserver-textdocument';
import { createInterfaceId, Injectable } from '@gitlab-org/di';
import { ConfigService } from '../config_service';
import { DocumentService } from '../document_service';
import { DocumentTransformerService } from '../document_transformer_service';
import { SupportedLanguagesService } from '../suggestion/supported_languages_service';
import { LsConnection } from '../external_interfaces';
import { log } from '../log';
import { TextDocumentChangeListenerType } from '../text_document_change_listener_type';
import { LruCache } from './lru_cache';
import { LRU_CACHE_BYTE_SIZE_LIMIT } from './helpers';

export interface AdvancedContextService {}

export const AdvancedContextService =
  createInterfaceId<AdvancedContextService>('AdvancedContextService');

@Injectable(AdvancedContextService, [
  ConfigService,
  LsConnection,
  DocumentService,
  DocumentTransformerService,
  SupportedLanguagesService,
])
export class DefaultAdvancedContextService implements AdvancedContextService {
  #configService: ConfigService;

  #documentTransformer: DocumentTransformerService;

  #supportedLanguagesService: SupportedLanguagesService;

  constructor(
    configService: ConfigService,
    connection: LsConnection,
    documentService: DocumentService,
    documentTransformer: DocumentTransformerService,
    supportedLanguagesService: SupportedLanguagesService,
  ) {
    this.#configService = configService;
    this.#documentTransformer = documentTransformer;
    this.#supportedLanguagesService = supportedLanguagesService;

    const subscription = documentService.onDocumentChange(async ({ document }, handlerType) => {
      await this.#updateAdvancedContext(document, handlerType);
    });
    connection.onShutdown(() => subscription.dispose());
  }

  /**
   * Currently updates the LRU cache with the most recently accessed files in the workspace.
   * We only update the advanced context for supported languages. We also ensure to run
   * documents through the `DocumentTransformer` to ensure the secret redaction is applied.
   */
  async #updateAdvancedContext(
    document: TextDocument,
    handlerType: TextDocumentChangeListenerType,
  ) {
    const lruCache = LruCache.getInstance(LRU_CACHE_BYTE_SIZE_LIMIT);
    // eslint-disable-next-line default-case
    switch (handlerType) {
      case TextDocumentChangeListenerType.onDidClose: {
        // We don't check if the language is supported for `onDidClose` because we want
        // always attempt to delete the file from the cache.
        const fileDeleted = lruCache.deleteFile(document.uri);
        if (fileDeleted) {
          log.debug(`Advanced Context: File ${document.uri} was deleted from the LRU cache`);
        } else {
          log.debug(`Advanced Context: File ${document.uri} was not found in the LRU cache`);
        }
        break;
      }
      case TextDocumentChangeListenerType.onDidChangeContent:
      case TextDocumentChangeListenerType.onDidSave:
      case TextDocumentChangeListenerType.onDidOpen:
      case TextDocumentChangeListenerType.onDidSetActive: {
        const languageEnabled = this.#supportedLanguagesService.isLanguageEnabled(
          document.languageId,
        );
        if (!languageEnabled) {
          return;
        }
        const context = this.#documentTransformer.getContext(
          document.uri,
          { line: 0, character: 0 },
          this.#configService.get().client.workspaceFolders ?? [],
          undefined,
        );

        if (!context) {
          log.debug(`Advanced Context: document context for ${document.uri} was not found`);
          return;
        }

        lruCache.updateFile(context);
        log.debug(`Advanced Context: uri ${document.uri} was updated in the MRU cache`);

        break;
      }
    }
  }
}
